// Shared key/value access backed by /api/storage (Netlify Blobs on the server).
// Every call falls back to localStorage so the PWA stays usable offline and a
// blip in the API never loses what someone just typed.

const api = (method, key, body, headers) =>
  fetch("/api/storage/" + encodeURIComponent(key), { method, body, headers });

const local = (key) => "hub:" + key;

export async function get(key, shared) {
  try {
    const r = await api("GET", key);
    if (r.ok) return await r.json();
    if (r.status !== 404) throw new Error("api " + r.status);
  } catch {
    /* fall through to the local copy */
  }
  const cached = localStorage.getItem(local(key));
  if (cached === null) throw new Error("not found");
  return { key, value: cached, shared: !!shared };
}

export async function set(key, value, shared) {
  let synced = false;
  try {
    synced = (await api("PUT", key, value)).ok;
  } catch {
    /* offline — the local write below still happens */
  }
  try {
    localStorage.setItem(local(key), value);
  } catch {
    /* quota or private mode; not fatal */
  }
  return { key, value, shared: !!shared, synced };
}

export async function remove(key) {
  try {
    await api("DELETE", key);
  } catch {
    /* ignore */
  }
  localStorage.removeItem(local(key));
  return { key, deleted: true };
}

// ---- list helpers (wall posts, chatter, inboxes) ----

export async function readList(key) {
  try {
    const r = await get(key, true);
    return r && r.value ? JSON.parse(r.value) : [];
  } catch {
    return [];
  }
}

// Appends server-side (read-modify-write in the function) so two people posting
// at once don't clobber each other the way a client-side rewrite would.
export async function appendItem(key, item) {
  const me = window.__me;
  if (me && !item.pid) item = { ...item, pid: me.pid };

  try {
    const r = await fetch("/api/storage/append/" + encodeURIComponent(key), {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(item),
    });
    if (r.ok) return (await r.json()).items || [];
  } catch {
    /* fall through to the local append */
  }

  let current = [];
  try {
    const raw = localStorage.getItem(local(key));
    current = raw ? JSON.parse(raw) : [];
  } catch {
    /* corrupt cache — start clean */
  }
  const next = [...current, item];
  try {
    localStorage.setItem(local(key), JSON.stringify(next));
  } catch {
    /* ignore */
  }
  return next;
}

export const stamp = (ts) =>
  new Date(ts).toLocaleDateString(undefined, { month: "short", day: "numeric" }) +
  " · " +
  new Date(ts).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
